/**
 * Flota — widoki pojazdów (lista kart, karta pojazdu): `fleet.web.veh.*`. Doklejone w `extra.ts`.
 * `fleetVehiclesUiEn` jest typowany względem `fleetVehiclesUiPl` — brak klucza = błąd kompilacji.
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const fleetVehiclesUiPl = {
  veh: {
    healthOf: 'Zdrowie pojazdu {plate}',
    awaitingDecision: 'Czeka na decyzję',
    onRouteSince: 'W trasie od {time}',
    costPerKm: 'Koszt/km (90 dni)',
    health: 'Zdrowie',
    leaseLimit: '{percent}% limitu',
    noLease: 'Brak aktywnej umowy leasingowej',
    searchPlaceholder: 'Szukaj: numer, marka, model, kierowca…',
    truncated: 'Pokazano pierwsze {max} pojazdów — zawęź wyszukiwanie.',
    tabsPrev: 'Przewiń zakładki w lewo',
    tabsNext: 'Przewiń zakładki w prawo',
    nowLive: 'teraz',
    leaseEnd: 'Koniec umowy leasingowej',
    tabs: { overview: 'Przegląd' },
    chip: { label: 'Szybkie filtry', all: 'Wszystkie', nearLimit: 'Blisko limitu', overdue: 'Po terminie', attention: 'Wymaga uwagi' },
    sort: { label: 'Sortowanie', plate: 'Numer rejestracyjny', health: 'Zdrowie (najsłabsze)', odometer: 'Przebieg (największy)', costPerKm: 'Koszt/km (najwyższy)', lease: 'Limit leasingu (najwyższy)' },
    chart: {
      title: 'Przebieg w czasie',
      description: 'Stan licznika z odczytów — jedyne źródło przebiegu.',
      summary: '{km} w wybranym okresie',
      range: 'Zakres wykresu',
      r90: '90 dni',
      r365: '12 miesięcy',
      rAll: 'Wszystko',
      empty: 'Za mało odczytów licznika, by narysować wykres.',
      aria: 'Stan licznika od {from} ({start}) do {to} ({end})',
      series: 'Stan licznika',
      table: 'Pokaż odczyty w tabeli',
    },
    activity: { title: 'Ostatnia aktywność', description: 'Karty, tankowania, koszty, zgłoszenia, checklisty i serwisy w jednej osi.', empty: 'Brak ostatniej aktywności tego pojazdu.' },
    act: {
      LOGBOOK: 'Karta drogowa: {status}',
      FUEL: 'Tankowanie {liters} l',
      EXPENSE: 'Koszt: {category}',
      CHECKLIST: 'Checklista: {phase}',
      SERVICE: 'Serwis: {type}',
      withWarnings: 'z ostrzeżeniami',
      criticalDefect: 'Usterka krytyczna',
      defect: 'Wykryto usterki',
      noDefects: 'Bez usterek',
    },
    healthCard: {
      title: 'Zdrowie pojazdu',
      description: 'Od 0 do 100, z jawnych kar punktowych.',
      decommissioned: 'Pojazd wycofany — wskaźnik nie jest liczony.',
      clean: 'Brak kar — pojazd bez wykrytych problemów.',
    },
    kpi: {
      odometer: 'Przebieg',
      odometerHint: 'Aktualny stan licznika',
      km: 'Km ostatnie {days} dni',
      kmIndicative: 'Orientacyjnie — brak odczytu z początku okresu',
      kmHint: 'Z odczytów licznika',
      consumption: 'Zużycie paliwa (l/100 km)',
      consumptionNone: 'Za mało kompletnych tankowań',
      consumptionIndicative: 'Orientacyjne — dane niekompletne',
      consumptionHint: 'Z kompletnych tankowań',
      costPerKm: 'Koszt/km ({days} dni)',
      costTotal: 'Razem {amount} (zaakceptowane)',
      lease: 'Limit leasingu',
      leaseRemaining: 'Zostało {km}',
      nextService: 'Najbliższy serwis',
    },
    deadlines: { title: 'Terminy', none: 'brak daty' },
  },
};

export const fleetVehiclesUiEn: Shape<typeof fleetVehiclesUiPl> = {
  veh: {
    healthOf: 'Health of vehicle {plate}',
    awaitingDecision: 'Awaiting decision',
    onRouteSince: 'On route since {time}',
    costPerKm: 'Cost/km (90 days)',
    health: 'Health',
    leaseLimit: '{percent}% of limit',
    noLease: 'No active lease',
    searchPlaceholder: 'Search: plate, make, model, driver…',
    truncated: 'Showing the first {max} vehicles — narrow your search.',
    tabsPrev: 'Scroll tabs left',
    tabsNext: 'Scroll tabs right',
    nowLive: 'now',
    leaseEnd: 'Lease end',
    tabs: { overview: 'Overview' },
    chip: { label: 'Quick filters', all: 'All', nearLimit: 'Near limit', overdue: 'Overdue', attention: 'Needs attention' },
    sort: { label: 'Sort', plate: 'Plate number', health: 'Health (weakest)', odometer: 'Mileage (highest)', costPerKm: 'Cost/km (highest)', lease: 'Lease limit (highest)' },
    chart: {
      title: 'Mileage over time',
      description: 'Odometer readings — the only source of mileage.',
      summary: '{km} in the selected period',
      range: 'Chart range',
      r90: '90 days',
      r365: '12 months',
      rAll: 'All',
      empty: 'Not enough odometer readings to draw a chart.',
      aria: 'Odometer from {from} ({start}) to {to} ({end})',
      series: 'Odometer',
      table: 'Show readings as a table',
    },
    activity: { title: 'Recent activity', description: 'Logbooks, fuel, costs, incidents, checklists and service in one timeline.', empty: 'No recent activity for this vehicle.' },
    act: {
      LOGBOOK: 'Logbook: {status}',
      FUEL: 'Refuelling {liters} l',
      EXPENSE: 'Cost: {category}',
      CHECKLIST: 'Checklist: {phase}',
      SERVICE: 'Service: {type}',
      withWarnings: 'with warnings',
      criticalDefect: 'Critical defect',
      defect: 'Defects found',
      noDefects: 'No defects',
    },
    healthCard: {
      title: 'Vehicle health',
      description: 'From 0 to 100, from explicit point penalties.',
      decommissioned: 'Vehicle decommissioned — the score is not calculated.',
      clean: 'No penalties — no problems detected.',
    },
    kpi: {
      odometer: 'Mileage',
      odometerHint: 'Current odometer reading',
      km: 'Km last {days} days',
      kmIndicative: 'Indicative — no reading from the start of the period',
      kmHint: 'From odometer readings',
      consumption: 'Fuel consumption (l/100 km)',
      consumptionNone: 'Not enough complete refuellings',
      consumptionIndicative: 'Indicative — incomplete data',
      consumptionHint: 'From complete refuellings',
      costPerKm: 'Cost/km ({days} days)',
      costTotal: 'Total {amount} (approved)',
      lease: 'Lease limit',
      leaseRemaining: '{km} left',
      nextService: 'Next service',
    },
    deadlines: { title: 'Deadlines', none: 'no date' },
  },
};
